import styles from './Register.module.css';

import classNames from 'classnames';
import { useId, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { X, CaretDown, CheckCircle } from '@phosphor-icons/react';

import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { isMobileDevice } from 'src/utils/device';
import { leaveSiteFor } from 'src/utils/leaveSite';
import { submitUnless } from 'src/utils/submitUnless';
import { isInternalPath } from 'src/utils/signInReturnTo';
import { normalizeLoginCode } from 'src/utils/emailLogin';
import { securityErrorMessage } from 'src/utils/securityError';
import { getApiErrorMessage } from 'src/utils/listingAvailability';
import { resolvePostRegisterDestination } from 'src/utils/postRegisterDestination';
import { citiesOf, chosenCityId, registerPayload, registerFormError } from 'src/utils/registerForm';

import { CONFIG } from 'src/config-global';
// From the module itself, not the barrel: the screen's tests stand the barrel in, and an error class
// is a thing to tell apart, not a hook to replace.
import { SessionNotKeptError, SignInUnconfirmedError } from 'src/queries/useConfirmUserLogin';
import { useRegister, useLocations, useResendUserOtp, useConfirmUserLogin } from 'src/queries';

import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';
import { ActionButton } from 'src/components/themed/ActionButton';

import { AccountRole } from 'src/types/userAuth';

type Step = 'form' | 'code';

/** Where a dealer writes when their city is not on the list: the platform adds cities on request (partners-spec §3.8). */
const SUPPORT_EMAIL = 'b.car.tech@proton.me';

/**
 * Registration (and login-for-an-existing-email) by email + OTP. Reads `plan` and an
 * optional listing `from` from the query string. Flow: form → code → on: once the code is
 * accepted the person is signed in and goes straight on, as from the sign-in screen — a buyer
 * back to the car they came from or to the catalog, a dealer to the dealer cabinet (see
 * resolvePostRegisterDestination; Tim's decision of 2026-10-31, app#368). No wallet is asked
 * for here: a buyer links one when they press Buy (decision no. 5).
 *
 * A dealer names their region as a country and a city from the platform's list (api#298,
 * partners-spec §3.8) — two dropdowns fed by `GET /public/locations`, required. A buyer has no
 * region (decision no. 13): the free-text `Region / country` field is gone for every plan.
 */
export const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') ?? undefined;
  const role =
    searchParams.get('plan') === AccountRole.DEALER ? AccountRole.DEALER : AccountRole.PERSONAL;
  const isDealer = role === AccountRole.DEALER;
  useDocumentTitle(isDealer ? 'Dealer sign up' : 'Sign up');

  const registerMutation = useRegister();
  const confirmMutation = useConfirmUserLogin();
  const resendMutation = useResendUserOtp();
  // Only the dealer plan asks for the list — a buyer's page makes no request.
  const locations = useLocations(isDealer);
  const listState = useQueryScreenState(locations);

  const [step, setStep] = useState<Step>('form');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [locationId, setLocationId] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [dealerTerms, setDealerTerms] = useState(false);
  const [reference, setReference] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);

  const countries = locations.data?.countries ?? [];
  const cities = citiesOf(countries, countryCode);
  // The city that counts is one of the chosen country's cities on the list as it is now: a city
  // switched off while the form was open, or left over from another country, is nobody's choice.
  const chosenLocationId = chosenCityId(cities, locationId);

  // The list the dealer can choose from is here (a refetch that fails keeps it, and must not take
  // it away); not here yet; failed before anything arrived; or arrived empty.
  const listReady = countries.length > 0;
  const listEmpty = listState === 'ready' && !listReady;
  const listFailed =
    !listReady && listState !== 'loading' && listState !== 'ready' && listState !== 'offline';
  const listOffline = !listReady && listState === 'offline';

  const destination = useMemo(
    () =>
      resolvePostRegisterDestination({
        role,
        from,
        isMobile: isMobileDevice(),
        dealerCabinetUrl: CONFIG.dealerCabinetUrl,
        telegramMiniAppUrl: CONFIG.telegramMiniAppUrl,
      }),
    [role, from]
  );

  // `from` is the address bar's: Close follows it only as an in-app path, as going on does.
  const handleClose = () => navigate(from && isInternalPath(from) ? from : '/', { replace: true });

  const handleCountryChange = (nextCountryCode: string) => {
    setCountryCode(nextCountryCode);
    // A city belongs to its country: changing the country empties the city.
    setLocationId('');
  };

  const handleRegister = async () => {
    setError('');
    const values = {
      email,
      role,
      name,
      locationId: chosenLocationId,
      marketingConsent,
      dealerTerms,
    };
    const formError = registerFormError(values);
    if (formError) {
      setError(formError);
      return;
    }
    try {
      const { reference: ref } = await registerMutation.mutateAsync(registerPayload(values));
      setReference(ref);
      setStep('code');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not send the code. Please try again.'));
    }
  };

  // By the time the code is accepted this screen is gone: the session the confirm stores puts the
  // shell's splash up at once, and the screen unmounts before `mutateAsync` resumes here — a last
  // step of its own was lost every time and the form came back empty (app#368). A navigation
  // still lands from here, as the sign-in screen's does. Both ways replace the registration in
  // the history, so Back does not return to a spent form.
  const goOn = () => {
    if (destination.kind === 'external') {
      leaveSiteFor(destination.url);
    } else {
      navigate(destination.path, { replace: true });
    }
  };

  const handleConfirm = async () => {
    setError('');
    if (!code.trim()) {
      setError('Enter the code from your email.');
      return;
    }
    try {
      await confirmMutation.mutateAsync({ code: normalizeLoginCode(code), reference });
    } catch (err) {
      // A browser that did not keep the session cookie (api#454) is told so, not sent on signed out;
      // a question that got no answer is told as that (reload — you may be signed in).
      if (err instanceof SessionNotKeptError || err instanceof SignInUnconfirmedError) {
        setError(err.message);
        return;
      }
      // Same OTP rails as sign-in: the refusal carries `attemptsLeft`, and the buyer gets to see it.
      setError(securityErrorMessage(err, 'That code did not work. Please try again.'));
      return;
    }
    goOn();
  };

  const handleResend = async () => {
    setError('');
    setResent(false);
    try {
      const { reference: ref } = await resendMutation.mutateAsync(reference);
      setReference(ref);
      setResent(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not resend the code.'));
    }
  };

  const countryPlaceholder = listReady
    ? 'Select a country'
    : listOffline
      ? 'No connection'
      : listFailed
        ? 'Could not load the list'
        : listEmpty
          ? 'No cities on the list yet'
          : 'Loading…';

  // A dealer cannot register without a city, and there is no city to choose while the list has
  // not arrived.
  const listUnavailable = isDealer && !listReady;
  // What holds each step's button holds Enter too (`submitUnless`).
  const cannotRegister = registerMutation.isPending || listUnavailable;
  const cannotConfirm = confirmMutation.isPending;

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" maxWidth="750px" gap={5} p={4} pb={20}>
        <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography as="h1" variant="title2" fontWeight="600" color="white">
            {isDealer ? 'Dealer sign up' : 'Sign up'}
          </Typography>
          <ActionButton onClick={handleClose} label="Close">
            <X size={24} color="white" />
          </ActionButton>
        </Stack>

        {/* Each step is a form, so Enter — Go on a phone keyboard — in a field does what the
            step's button does (app#366). `noValidate`: the screen checks the fields itself and
            says what is missing in its own words, as it did when the button was the only way. The
            other buttons inside stay `type="button"`: a checkbox before Continue that submitted
            would be what Enter presses. */}
        {step === 'form' ? (
          <form noValidate onSubmit={submitUnless(cannotRegister, handleRegister)}>
            <Stack gap={4}>
              <Field label="Email" required>
                {(id) => (
                  <input
                    id={id}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    className={styles.input}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label={isDealer ? 'Company name' : 'Name'}>
                {(id) => (
                  <input
                    id={id}
                    type="text"
                    autoComplete={isDealer ? 'organization' : 'name'}
                    className={styles.input}
                    placeholder="Optional"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
              </Field>

              {isDealer ? (
                <>
                  <Field label="Country" required>
                    {(id) => (
                      <Select
                        id={id}
                        required
                        value={countryCode}
                        disabled={!listReady}
                        onChange={(e) => handleCountryChange(e.target.value)}
                      >
                        <option value="">{countryPlaceholder}</option>
                        {countries.map((country) => (
                          <option key={country.countryCode} value={country.countryCode}>
                            {country.countryName}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field
                    label="City"
                    required
                    hint={
                      <>
                        Your city is not on the list? Write to{' '}
                        <a className={styles.inlineLink} href={`mailto:${SUPPORT_EMAIL}`}>
                          {SUPPORT_EMAIL}
                        </a>{' '}
                        — we add cities on request.
                      </>
                    }
                  >
                    {(id, hintId) => (
                      <Select
                        id={id}
                        required
                        aria-describedby={hintId}
                        value={chosenLocationId}
                        disabled={!countryCode}
                        onChange={(e) => setLocationId(e.target.value)}
                      >
                        <option value="">
                          {countryCode ? 'Select a city' : 'Choose a country first'}
                        </option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id}>
                            {city.city}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  {listFailed ? (
                    <ErrorText>
                      Could not load the list of countries and cities.{' '}
                      <button
                        type="button"
                        className={styles.inlineButton}
                        onClick={() => locations.refetch()}
                      >
                        Try again
                      </button>
                    </ErrorText>
                  ) : null}
                  {listOffline ? (
                    <Typography color="lightWhite" variant="small">
                      No connection. The list of countries and cities will load once you are back
                      online.
                    </Typography>
                  ) : null}
                  {listEmpty ? (
                    <ErrorText>
                      The list of cities is empty right now. Write to{' '}
                      <a className={styles.inlineLink} href={`mailto:${SUPPORT_EMAIL}`}>
                        {SUPPORT_EMAIL}
                      </a>{' '}
                      and we will add yours.
                    </ErrorText>
                  ) : null}
                </>
              ) : null}

              {isDealer ? (
                <Stack gap={2} className={styles.earlyAccess}>
                  <Typography color="white" fontWeight="600" variant="large">
                    Early access — free for now
                  </Typography>
                  <Typography color="lightWhite" variant="small">
                    Dealer accounts are free while we finish billing. After launch it becomes €89/mo
                    — we&apos;ll let you know well in advance.
                  </Typography>
                  <Checkbox checked={dealerTerms} onToggle={() => setDealerTerms((v) => !v)}>
                    I accept the early-access terms.
                  </Checkbox>
                </Stack>
              ) : null}

              <Checkbox checked={marketingConsent} onToggle={() => setMarketingConsent((v) => !v)}>
                Send me product updates and offers by email.
              </Checkbox>

              {error ? <ErrorText>{error}</ErrorText> : null}

              <Button
                type="submit"
                title="Continue"
                variant="accent"
                loading={registerMutation.isPending}
                disabled={cannotRegister}
              />
              <Typography color="lightWhite" variant="small" textAlign="center">
                By continuing you agree to our Terms and Privacy Policy.
              </Typography>
            </Stack>
          </form>
        ) : null}

        {step === 'code' ? (
          <form noValidate onSubmit={submitUnless(cannotConfirm, handleConfirm)}>
            <Stack gap={4}>
              <Typography color="lightWhite">
                We emailed a code to <span className={styles.email}>{email.trim()}</span>. Enter it
                below.
              </Typography>
              <Field label="Verification code" required>
                {(id) => (
                  <input
                    id={id}
                    type="text"
                    // Four capital letters and digits (api `generateCode(4)`): the full keyboard,
                    // asked for capitals — `numeric` gave iPhones a digit pad that cannot type
                    // the letters — and what was typed is sent in capitals (`normalizeLoginCode`).
                    inputMode="text"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="one-time-code"
                    required
                    className={styles.input}
                    placeholder="A1B2"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                )}
              </Field>

              {resent ? (
                <Typography color="lightWhite" variant="small">
                  A new code is on its way.
                </Typography>
              ) : null}
              {error ? <ErrorText>{error}</ErrorText> : null}

              <Button
                type="submit"
                title="Confirm"
                variant="accent"
                loading={confirmMutation.isPending}
                disabled={cannotConfirm}
              />
              <Stack flexDirection="row" gap={2} justifyContent="center">
                <Typography color="lightWhite" variant="small">
                  Didn&apos;t get it?
                </Typography>
                <button className={styles.linkButton} onClick={handleResend} type="button">
                  <Typography color="accent" variant="small">
                    {resendMutation.isPending ? 'Sending...' : 'Resend code'}
                  </Typography>
                </button>
              </Stack>
            </Stack>
          </form>
        ) : null}
      </Stack>
    </div>
  );
};

/**
 * A labelled field: the label is a real `<label>` pointing at the control, so a screen reader names
 * the control by it and a tap on the label focuses it; an optional hint is wired to the control by
 * `aria-describedby`. The control is rendered by the child with the ids this component owns.
 */
const Field = ({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  children: (id: string, hintId: string | undefined) => React.ReactNode;
}) => {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <Stack gap={1}>
      <label htmlFor={id} className={styles.label}>
        <Typography as="span" color="lightWhite" variant="small">
          {label}
          {required ? ' *' : ''}
        </Typography>
      </label>
      {children(id, hintId)}
      {hint ? (
        <Typography as="p" id={hintId} color="lightWhite" variant="small">
          {hint}
        </Typography>
      ) : null}
    </Stack>
  );
};

/**
 * A native select in the field's clothes, with the brand's own caret (Phosphor, as the catalog's
 * fold draws it): iOS Safari paints no caret on a styled select, so `appearance: none` takes the
 * browser's away everywhere and the icon stands in for all of them. While nothing is chosen the first
 * option reads as a placeholder — `text / muted`, like an input's — and not as a value.
 */
const Select = ({
  value,
  children,
  ...props
}: Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'value'> & {
  value: string;
}) => (
  <span className={styles.selectWrap}>
    <select
      {...props}
      value={value}
      className={classNames(styles.input, styles.select, !value && styles.selectPlaceholder)}
    >
      {children}
    </select>
    <CaretDown aria-hidden size={16} className={styles.selectCaret} />
  </span>
);

const Checkbox = ({
  checked,
  onToggle,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <button type="button" className={styles.checkboxRow} onClick={onToggle}>
    <span className={classNames(styles.checkbox, checked && styles.checkboxChecked)}>
      {checked ? <CheckCircle size={18} color="#010101" weight="fill" /> : null}
    </span>
    <Typography color="white" variant="small" textAlign="left">
      {children}
    </Typography>
  </button>
);

const ErrorText = ({ children }: { children: React.ReactNode }) => (
  <Typography color="danger" variant="small" className={styles.error}>
    {children}
  </Typography>
);
